import { AppMediaSettings } from 'app/appMediaSettings';
import { useMprcTheme } from 'components/MprcThemeProvider';
import useDidUpdateEffect from 'hooks/useNoInitialEffect';
import React, { ComponentType, CSSProperties, MouseEvent, ReactElement, ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactPlayer, { Config } from 'react-player';
import { OnProgressProps } from 'react-player/base';
import { PlayerTracker } from 'services/playerTracker';

interface CCOption {
    id: number;
    label: string;
}

interface SpeedOption {
    value: number;
    label: string;
}

interface Styles {
    [key: string]: CSSProperties;
}

type MprcReactPlayerControls = {
    seek: boolean;
    playTime: boolean;
    closeCaption: boolean;
    speed: boolean;
    volume: boolean;
    fullscreen: boolean;
};

const accentColor = '#ef4444';

const playerStyles = (style: CSSProperties, controls: MprcReactPlayerControls, showControls: boolean, isFullscreen: boolean): Styles => ({
    container: {
        width: "100%",
        height: "100%",
        ...style
    },
    videoContainer: {
        width: "100%",
        height: "100%"
    },
    controls: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
        padding: '20px 16px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        opacity: showControls ? 1 : 0,
        transition: isFullscreen ? 'opacity 0.8s ease' : 'opacity 0.3s ease',
        zIndex: 2  // Garantir que os controles fiquem acima do overlay
    },
    controlButton: {
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    activeButton: {
        backgroundColor: accentColor
    },
    icon: {
        width: '20px',
        height: '20px',
        fill: 'currentColor'
    },
    progressContainer: {
        flex: 1,
        height: '4px',
        backgroundColor: 'rgba(255,255,255,0.3)',
        borderRadius: '2px',
        margin: '0 12px',
        cursor: 'pointer',
        position: 'relative',
        visibility: controls.seek ? "visible" : "hidden"
    },
    progressBar: {
        height: '100%',
        backgroundColor: accentColor,
        borderRadius: '2px',
        transition: 'width 0.1s'
    },
    timeDisplay: {
        color: 'white',
        fontSize: '12px',
        fontFamily: 'monospace',
        whiteSpace: 'nowrap',
        visibility: controls.playTime ? "visible" : "hidden"
    },
    volumeContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        visibility: controls.volume ? "visible" : "hidden"
    },
    volumeSlider: {
        width: '60px',
        height: '4px',
        backgroundColor: 'rgba(255,255,255,0.3)',
        borderRadius: '2px',
        cursor: 'pointer',
        position: 'relative'
    },
    volumeFill: {
        height: '100%',
        backgroundColor: 'white',
        borderRadius: '2px',
        transition: 'width 0.1s'
    },
    menuContainer: {
        position: 'relative'
    },
    menu: {
        position: 'absolute',
        bottom: '100%',
        right: 0,
        marginBottom: '8px',
        backgroundColor: 'rgba(0,0,0,0.9)',
        borderRadius: '4px',
        padding: '8px 0',
        minWidth: '120px',
        zIndex: 1000
    },
    menuOption: {
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '8px 16px',
        fontSize: '14px',
        color: 'white',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    speedButton: {
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        padding: '8px 12px',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
        fontSize: '12px',
        fontFamily: 'monospace',
        fontWeight: 'bold',
    },
    subtitleOverlay: {
        position: 'absolute',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0,0,0,0.75)',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '4px',
        textAlign: 'center'
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px'
    },
    rangeInput: {
        width: '80px'
    },
    select: {
        padding: '4px 8px',
        border: '1px solid #ccc',
        borderRadius: '4px'
    },
    /* Cursor em fullscreen */
    ".video-container:fullscreen": {
        cursor: 'none'
    },
    ".video-container:fullscreen.show-cursor": {
        cursor: 'default'
    }
});

// Interface para métodos expostos via ref
export interface MprcReactPlayerRef {
    play: () => void;
    pause: () => void;
    stop: () => void;
    seekTo: (seconds: number) => void;
    setVolume: (volume: number) => void;
    setPlaybackRate: (rate: number) => void;
    getCurrentTime: () => number;
    getDuration: () => number;
    getPlaying: () => boolean;
    getMuted: () => boolean;
    getVolume: () => number;
    getPlaybackRate: () => number;
    togglePlay: () => void;
    toggleMute: () => void;
    enterFullscreen: () => Promise<void>;
    exitFullscreen: () => Promise<void>;
    getInternalPlayer: (key?: string) => Record<string, any>;
}

interface IProps {
    id: string;
    videoId?: string;
    config?: Config;
    url?: string;
    title?: string | null;
    playing?: boolean;
    autoPlay?: boolean;
    loop?: boolean;
    volume?: number;
    muted?: boolean;
    playbackRate?: number;
    width?: string | number;
    height?: string | number;
    progressInterval?: number;
    playsinline?: boolean;
    playIcon?: ReactElement;
    previewTabIndex?: number | null;
    pip?: boolean;
    stopOnUnmount?: boolean;
    light?: boolean | string | ReactElement;
    fallback?: ReactElement;
    className?: string;
    style?: CSSProperties;
    wrapper?: ComponentType<{ children: ReactNode; }>;

    onReady?: (player: ReactPlayer) => void;
    onStart?: () => void;
    onPlay?: () => void;
    onPause?: () => void;
    onBuffer?: () => void;
    onBufferEnd?: () => void;
    onEnded?: () => void;
    onClickPreview?: (event: any) => void;
    onEnablePIP?: () => void;
    onDisablePIP?: () => void;
    onError?: (
        error: any,
        data?: any,
        hlsInstance?: any,
        hlsGlobal?: any
    ) => void;
    onDuration?: (duration: number) => void;
    onSeek?: (seconds: number) => void;
    onProgress?: (state: OnProgressProps) => void;

    controls?: MprcReactPlayerControls,
    speedOptions?: SpeedOption[];
    autoCloseCaptionLang?: string | null;
    onVolumeChange?: (volume: number) => void;
    onPlaybackRateChange?: (rate: number) => void;
    onCCChange?: (ccLang?: string) => void;
}

const MprcReactPlayerComponent = React.forwardRef<MprcReactPlayerRef, IProps>((props, ref) => {
    const { theme } = useMprcTheme();

    const {
        onProgress, onDuration, onEnded, onPause, onPlay, onReady, onVolumeChange, onPlaybackRateChange, onCCChange,
        id,
        url,
        videoId,
        title,
        autoPlay = false,
        playing: initialPlaying = false,
        muted: initialMuted = false,
        volume: initialVolume = AppMediaSettings.getVolume(),
        playbackRate: initialPlaybackRate = 1,
        autoCloseCaptionLang,
        style = {},
        speedOptions = [
            { value: 0.25, label: '0.25x' },
            { value: 0.5, label: '0.5x' },
            { value: 0.75, label: '0.75x' },
            { value: 1, label: '1x' },
            { value: 1.25, label: '1.25x' },
            { value: 1.5, label: '1.5x' },
            { value: 1.75, label: '1.75x' },
            { value: 2, label: '2x' }
        ],
        controls = {
            closeCaption: true,
            fullscreen: true,
            seek: true,
            speed: true,
            playTime: true,
            volume: true
        },
        ...rest
    } = props;

    const isTracked = useMemo(() =>
        (props.config?.file?.forceAudio ?? false) == false
        && videoId != null
        && url != null && typeof (url) == "string"
        && !url.includes("youtube") && !url.includes('youtu.be')
        && !url.includes("vimeo")
        && !url.includes("GetVideoPlanExceeded")
        , [url]);

    const videoKey = useRef(`video-${videoId ?? Date.now()}`);

    var playerAnalytics = useRef<PlayerTracker | null>(null);
    const playerRef = useRef<ReactPlayer>(null);
    const [ready, setReady] = useState<boolean>(false);
    const [playing, setPlaying] = useState<boolean>(false);
    const [volume, setVolume] = useState<number>(initialVolume);
    const [muted, setMuted] = useState<boolean>(initialMuted);
    const [playbackRate, setPlaybackRate] = useState<number>(initialPlaybackRate);
    const [played, setPlayed] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [showControls, setShowControls] = useState<boolean>(false);
    const [showCCMenu, setShowCCMenu] = useState<boolean>(false);
    const [selectedTrack, setSelectedTrack] = useState<number>(-1);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [showSpeedMenu, setShowSpeedMenu] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [hideControlsTimeout, setHideControlsTimeout] = useState<NodeJS.Timeout | null>(null);

    const styles = useMemo(() => {
        return playerStyles(style, controls, showControls, isFullscreen);
    }, [style, controls, showControls, isFullscreen]);


    // Expor métodos via ref
    useImperativeHandle(ref, () => ({
        play: () => {
            setPlaying(true);
        },
        pause: () => {
            setPlaying(false);
        },
        stop: () => {
            setPlaying(false);
            playerRef.current?.seekTo(0);
        },
        seekTo: (seconds: number) => {
            playerRef.current?.seekTo(seconds);
        },
        setVolume: (newVolume: number) => {
            const clampedVolume = Math.max(0, Math.min(1, newVolume));
            setVolume(clampedVolume);
            setMuted(false);
            AppMediaSettings.setVolume(clampedVolume);
            onVolumeChange?.(clampedVolume);
        },
        setPlaybackRate: (rate: number) => {
            setPlaybackRate(rate);
            onPlaybackRateChange?.(rate);
        },
        getInternalPlayer: (key?: string) => playerRef.current!.getInternalPlayer(key),
        getCurrentTime: () => currentTime,
        getDuration: () => duration,
        getPlaying: () => playing,
        getMuted: () => muted,
        getVolume: () => volume,
        getPlaybackRate: () => playbackRate,
        togglePlay: () => {
            setPlaying(!playing);
        },
        toggleMute: () => {
            setMuted(!muted);
        },
        enterFullscreen: async () => {
            const element = document.querySelector(id) as HTMLElement;
            if (element && !document.fullscreenElement) {
                await element.requestFullscreen();
            }
        },
        exitFullscreen: async () => {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
        }
    }), [playing, muted, volume, playbackRate, currentTime, duration, onVolumeChange, onPlaybackRateChange]);


    const disableMediaSession = () => {
        if ('mediaSession' in navigator) {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: title ?? undefined,
                artwork: props.config?.file?.attributes?.poster ? [{ src: props.config?.file?.attributes?.poster }] as MediaImage[] : undefined
            });

            // Lista de todas as ações possíveis
            const actions: MediaSessionAction[] = [
                'play', 'pause', 'stop', 'seekbackward', 'seekforward',
                'seekto', 'previoustrack', 'nexttrack', 'skipad'
            ];

            actions.forEach(action => {
                try {
                    navigator.mediaSession.setActionHandler(action, function () { });
                } catch {
                    // Ignora erros para ações não suportadas
                }
            });
        }
    };

    // Extrair tracks do config
    const ccOptions: CCOption[] = useMemo(() => {
        const tracks = props.config?.file?.tracks || [];
        const subtitleTracks = tracks.filter(track =>
            track.kind === 'subtitles' || track.kind === 'captions'
        );

        const options: CCOption[] = [{ id: -1, label: 'Desligado' }];

        subtitleTracks.forEach((track, index) => {
            options.push({
                id: index,
                label: track.label || `${track.srcLang} (${track.kind})`
            });
        });

        return options;
    }, [props.config]);

    // Função para iniciar o timer de ocultar controles
    const startHideControlsTimer = (): void => {
        clearHideControlsTimer();
        const timeout = setTimeout(() => {
            if (isFullscreen) {
                setShowControls(false);
            }
        }, 3000); // 3 segundos
        setHideControlsTimeout(timeout);
    };

    // Função para limpar o timer
    const clearHideControlsTimer = (): void => {
        if (hideControlsTimeout) {
            clearTimeout(hideControlsTimeout);
            setHideControlsTimeout(null);
        }
    };

    // Função para lidar com movimento do mouse em fullscreen
    const handleMouseMove = (): void => {
        if (isFullscreen) {
            setShowControls(true);
            startHideControlsTimer();
        }
    };

    // Função para lidar com mouse enter/leave
    const handleMouseEnter = (): void => {
        setShowControls(true);
        if (isFullscreen) {
            startHideControlsTimer();
        }
    };

    const handleMouseLeave = (): void => {
        if (!isFullscreen) {
            setShowControls(false);
        }
        // Em fullscreen, deixar o timer cuidar de ocultar
    };

    // Event handlers
    const handlePlayPause = (newPlaying: boolean): void => {
        setPlaying(newPlaying);

        if (newPlaying) {
            onPlay?.();
        } else {
            onPause?.();
        }
    };

    const handleSeek = (e: MouseEvent<HTMLDivElement>): void => {
        e.stopPropagation();

        const rect = e.currentTarget.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        setPlayed(pos);
        playerRef.current?.seekTo(pos);
    };

    const handleVolumeChange = (e: MouseEvent<HTMLDivElement>): void => {
        e.stopPropagation();

        const rect = e.currentTarget.getBoundingClientRect();
        const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        setVolume(pos);
        setMuted(false);
        AppMediaSettings.setVolume(pos);
        onVolumeChange?.(pos);
    };

    const handleMute = (e: MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();

        const newMuted = !muted;
        setMuted(newMuted);
        onVolumeChange?.(newMuted ? 0 : volume);
    };

    const handleFullscreenChange = (): void => {
        const isCurrentlyFullscreen = !!document.fullscreenElement;
        setIsFullscreen(isCurrentlyFullscreen);

        if (isCurrentlyFullscreen) {
            // Comportamento específico para fullscreen
            startHideControlsTimer();
        }
    };

    const handleCCToggle = (e: MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();
        setShowCCMenu(!showCCMenu);
        setShowSpeedMenu(false);
    };

    const handleCCSelect = (e: MouseEvent<HTMLButtonElement>, trackId: number): void => {
        e.stopPropagation();
        setSelectedTrack(trackId);
        setShowCCMenu(false);

        // Aguardar um frame para garantir que o estado foi atualizado
        requestAnimationFrame(() => {
            const videoElement = playerRef.current?.getInternalPlayer() as HTMLVideoElement;

            if (videoElement) {
                // Aguardar as text tracks estarem disponíveis
                const checkAndSetTracks = () => {
                    if (videoElement.textTracks && videoElement.textTracks.length > 0) {
                        // Desabilitar todas as tracks
                        Array.from(videoElement.textTracks).forEach((track) => {
                            track.mode = 'disabled';
                        });

                        // Habilitar track selecionada
                        if (trackId >= 0 && trackId < videoElement.textTracks.length) {
                            const selectedTextTrack = videoElement.textTracks[trackId];
                            selectedTextTrack.mode = 'showing';

                            onCCChange?.(selectedTextTrack.language);
                        } else {
                            onCCChange?.(undefined);
                        }
                    } else {
                        // Se as tracks ainda não estão disponíveis, tentar novamente em breve
                        setTimeout(checkAndSetTracks, 100);
                    }
                };

                checkAndSetTracks();
            }
        });

    };

    const handleSpeedToggle = (e: MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();
        setShowSpeedMenu(!showSpeedMenu);
        setShowCCMenu(false);
    };

    const handleSpeedSelect = (e: MouseEvent<HTMLButtonElement>, speed: number): void => {
        e.stopPropagation();
        setPlaybackRate(speed);
        setShowSpeedMenu(false);
        onPlaybackRateChange?.(speed);
    };

    const handleFullscreen = async (e: MouseEvent<HTMLButtonElement>): Promise<void> => {
        e.stopPropagation();
        const element = document.getElementById(id) as HTMLElement;
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            } else {
                await element?.requestFullscreen();
            }
        } catch (error) {
            console.error('Erro ao alterar fullscreen:', error);
        }
    };

    const handleProgress = (progress: OnProgressProps): void => {
        setPlayed(progress.played);
        setCurrentTime(progress.playedSeconds);
        onProgress?.(progress);
    };

    const handleDuration = (newDuration: number): void => {
        setDuration(newDuration);
        onDuration?.(newDuration);
    };

    const handleEnded = (): void => {
        onEnded?.();
    };

    const formatTime = (seconds: number): string => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleButtonHover = (e: MouseEvent<HTMLButtonElement>, isHovering: boolean): void => {
        const target = e.target as HTMLButtonElement;
        if (isHovering) {
            target.style.backgroundColor = 'rgba(255,255,255,0.2)';
        } else {
            target.style.backgroundColor = 'transparent';
        }
    };

    const handleMenuOptionHover = (
        e: MouseEvent<HTMLButtonElement>,
        isHovering: boolean,
        isActive: boolean
    ): void => {
        const target = e.target as HTMLButtonElement;
        if (isHovering && !isActive) {
            target.style.backgroundColor = 'rgba(255,255,255,0.1)';
        } else if (!isActive) {
            target.style.backgroundColor = 'transparent';
        }
    };

    // Manipula CLICKS fora da área de vídeo.
    const handleClickOutside = (): void => {
        setShowCCMenu(false);
        setShowSpeedMenu(false);
    };

    // Escuta Eventos
    function listenEvents() {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
    }

    // Remove escuta de eventos
    function unlistenEvents() {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        clearHideControlsTimer();
    }

    // Se 'props.playing' mudar
    useDidUpdateEffect(() => {
        if (initialPlaying != null && initialPlaying != playing) {
            handlePlayPause(initialPlaying);
        }
    }, [initialPlaying]);

    // Faz o Analytics Tracking, se necessário
    useEffect(() => {
        disableMediaSession();

        if (isTracked) {
            const videoElement = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
            if (videoElement != null) {
                playerAnalytics.current = new PlayerTracker({
                    mediaUrl: url as string,
                    fileId: videoId!
                });
                playerAnalytics.current.observeMedia(videoElement);
            }
        }
        else {
            playerAnalytics.current = null;
        }

        return () => {
            /// Cleanup Analytics
            if (playerAnalytics.current != null) playerAnalytics.current.dispose();
        };

    }, [playerRef.current, isTracked]);


    // Inicializar legenda padrão
    useEffect(() => {
        const tracks = props.config?.file?.tracks || [];
        const subtitleTracks = tracks.filter(track =>
            track.kind === 'subtitles' || track.kind === 'captions'
        );

        // Encontrar legenda padrão
        const defaultTrackIndex = subtitleTracks.findIndex(track => track.default || track.srcLang == autoCloseCaptionLang);
        if (defaultTrackIndex !== -1) {
            setSelectedTrack(defaultTrackIndex);
        }
    }, [props.config]);

    // Aplicar configuração da legenda  quando o player estiver pronto
    useEffect(() => {
        if (selectedTrack !== -1) {
            const applySubtitleTrackConfig = () => {
                const videoElement = playerRef.current?.getInternalPlayer() as HTMLVideoElement;

                if (videoElement && videoElement.textTracks && videoElement.textTracks.length > 0) {
                    // Desabilitar todas as tracks
                    Array.from(videoElement.textTracks).forEach((track) => {
                        track.mode = 'disabled';
                    });

                    // Habilitar track selecionada
                    if (selectedTrack >= 0 && selectedTrack < videoElement.textTracks.length) {
                        videoElement.textTracks[selectedTrack].mode = 'showing';
                    }
                }
            };

            // Tentar aplicar imediatamente
            applySubtitleTrackConfig();

            // E também após um pequeno delay para garantir
            setTimeout(applySubtitleTrackConfig, 500);
        }
    }, [selectedTrack, duration]); // Usar duration como trigger para quando o vídeo estiver carregado

    // Inicializa
    useEffect(() => {
        listenEvents();

        return () => {
            unlistenEvents();
        };
    }, []);

    ///
    return (
        <div style={styles.container}>
            <div
                id={id}
                className={`video-container ${showControls && isFullscreen ? 'show-cursor' : ''}`}
                style={styles.videoContainer}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
            >

                <ReactPlayer
                    key={videoKey.current}
                    {...rest}
                    url={url}
                    ref={playerRef}
                    playing={playing}
                    volume={volume}
                    muted={muted}
                    controls={false}
                    stopOnUnmount={true}
                    playbackRate={playbackRate}
                    onProgress={handleProgress}
                    onDuration={handleDuration}
                    onEnded={handleEnded}
                    onReady={(player) => {
                        // Aguardar um pouco para as text tracks serem carregadas
                        if (!ready) {
                            setTimeout(() => {
                                const videoElement = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
                                if (videoElement && videoElement.textTracks) {
                                    // Aplicar configuração padrão se houver
                                    if (selectedTrack >= 0 && selectedTrack < videoElement.textTracks.length) {
                                        videoElement.textTracks[selectedTrack].mode = 'showing';
                                    }
                                }

                            }, 1000);

                            setReady(true);
                            setPlaying(autoPlay || initialPlaying);

                            onReady?.(player);
                        }
                    }}
                />

                {/* Overlay clicável para play/pause */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                    }}
                    onClick={() => ready && handlePlayPause(!playing)}
                    aria-label={playing ? theme.MprcReactPlayer.pauseTitle : theme.MprcReactPlayer.playTitle}
                >

                    {/* Ícone de play central (aparece apenas quando pausado) */}
                    {!playing && ready && (
                        <div
                            style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                borderRadius: '50%',
                                width: '80px',
                                height: '80px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: showControls ? 0.9 : 0.6,
                                transition: 'opacity 0.3s ease',
                                pointerEvents: 'none'
                            }}
                        >
                            <svg
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    fill: 'white',
                                    marginLeft: '4px' // Ajuste visual para centralizar o ícone
                                }}
                                viewBox="0 0 24 24"
                            >
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    )}

                    {/* Ícone de loading central (aparece apenas enquanto não está pronto) */}
                    {!ready && (
                        <div
                            style={{
                                backgroundColor: 'transparent',
                                borderRadius: '50%',
                                width: '80px',
                                height: '80px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: showControls ? 0.9 : 0.6,
                                transition: 'opacity 0.3s ease',
                                pointerEvents: 'none'
                            }}
                        >
                            <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="40" cy="40" r="30" fill="none" stroke="#3b82f6" stroke-width="6" stroke-linecap="round" stroke-dasharray="140 50">
                                    <animateTransform
                                        attributeName="transform"
                                        type="rotate"
                                        from="0 40 40"
                                        to="360 40 40"
                                        dur="1s"
                                        repeatCount="indefinite" />
                                </circle>
                            </svg>
                        </div>
                    )}
                </div>

                {/* Controles Customizados */}
                <div style={styles.controls}>
                    {/* Play/Pause Button */}
                    <button
                        disabled={!ready}
                        onClick={() => handlePlayPause(!playing)}
                        style={styles.controlButton}
                        onMouseEnter={(e) => handleButtonHover(e, true)}
                        onMouseLeave={(e) => handleButtonHover(e, false)}
                        type="button"
                        title={playing ? theme.MprcReactPlayer.pauseTitle : theme.MprcReactPlayer.playTitle}
                    >
                        {playing ? (
                            <svg style={styles.icon} viewBox="0 0 24 24">
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                            </svg>
                        ) : (
                            <svg style={styles.icon} viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        )}
                    </button>

                    {/* Progress Bar */}
                    <div
                        style={styles.progressContainer}
                        onClick={handleSeek}
                        role="progressbar"
                        aria-valuenow={played * 100}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        title={theme.MprcReactPlayer.progressTitle}
                    >
                        <div
                            style={{
                                ...styles.progressBar,
                                width: `${played * 100}%`
                            }}
                        />
                    </div>

                    {/* Time Display */}
                    <div style={styles.timeDisplay}>
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </div>

                    {/* Volume Controls */}
                    <div style={styles.volumeContainer}>
                        <button
                            disabled={!ready}
                            onClick={handleMute}
                            style={styles.controlButton}
                            onMouseEnter={(e) => handleButtonHover(e, true)}
                            onMouseLeave={(e) => handleButtonHover(e, false)}
                            type="button"
                            title={muted ? theme.MprcReactPlayer.mutedOffTitle : theme.MprcReactPlayer.muteOnTitle}
                        >
                            <svg style={styles.icon} viewBox="0 0 24 24">
                                {muted || volume === 0 ? (
                                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                                ) : (
                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
                                )}
                            </svg>
                        </button>

                        <div
                            style={styles.volumeSlider}
                            onClick={handleVolumeChange}
                            role="slider"
                            aria-valuenow={volume * 100}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            title={theme.MprcReactPlayer.volumeTitle}
                        >
                            <div
                                style={{
                                    ...styles.volumeFill,
                                    width: `${muted ? 0 : volume * 100}%`
                                }}
                            />
                        </div>

                        {/* CC Button */}
                        <div style={styles.menuContainer}>
                            <button
                                disabled={!ready}
                                onClick={handleCCToggle}
                                style={{
                                    ...styles.controlButton,
                                    backgroundColor: selectedTrack >= 0 ? accentColor : 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                    if (selectedTrack < 0) {
                                        handleButtonHover(e, true);
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (selectedTrack < 0) {
                                        handleButtonHover(e, false);
                                    }
                                }}
                                type="button"
                                title={theme.MprcReactPlayer.closeCaptionTitle}
                                aria-expanded={showCCMenu}
                            >
                                <svg style={styles.icon} viewBox="0 0 24 24">
                                    <path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V6c0-1.1-.89-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z" />
                                </svg>
                            </button>

                            {/* CC Menu */}
                            {controls.closeCaption && showCCMenu && (
                                <div style={styles.menu} role="menu">
                                    {ccOptions.map((option) => (
                                        <button
                                            key={option.id}
                                            disabled={!ready}
                                            onClick={(e) => handleCCSelect(e, option.id)}
                                            style={{
                                                ...styles.menuOption,
                                                backgroundColor: selectedTrack === option.id ? accentColor : 'transparent'
                                            }}
                                            onMouseEnter={(e) => handleMenuOptionHover(e, true, selectedTrack === option.id)}
                                            onMouseLeave={(e) => handleMenuOptionHover(e, false, selectedTrack === option.id)}
                                            type="button"
                                            role="option"
                                            aria-selected={selectedTrack === option.id}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Speed Control Button */}
                        {
                            controls.speed == true &&
                            <div style={styles.menuContainer}>
                                <button
                                    disabled={!ready}
                                    onClick={handleSpeedToggle}
                                    style={styles.speedButton}
                                    onMouseEnter={(e) => handleButtonHover(e, true)}
                                    onMouseLeave={(e) => handleButtonHover(e, false)}
                                    type="button"
                                    title={theme.MprcReactPlayer.speedButtomTitle}
                                    aria-expanded={showSpeedMenu}
                                >
                                    {playbackRate}x
                                </button>

                                {/* Speed Menu */}
                                {showSpeedMenu && (
                                    <div style={styles.menu} role="menu">
                                        {speedOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                disabled={!ready}
                                                onClick={(e) => handleSpeedSelect(e, option.value)}
                                                style={{
                                                    ...styles.menuOption,
                                                    backgroundColor: playbackRate === option.value ? accentColor : 'transparent'
                                                }}
                                                onMouseEnter={(e) => handleMenuOptionHover(e, true, playbackRate === option.value)}
                                                onMouseLeave={(e) => handleMenuOptionHover(e, false, playbackRate === option.value)}
                                                type="button"
                                                role="option"
                                                aria-selected={playbackRate === option.value}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        }
                    </div>

                    {/* Fullscreen Button */}
                    {controls.fullscreen &&
                        <button
                            disabled={!ready}
                            onClick={handleFullscreen}
                            style={styles.controlButton}
                            onMouseEnter={(e) => handleButtonHover(e, true)}
                            onMouseLeave={(e) => handleButtonHover(e, false)}
                            type="button"
                            title={isFullscreen ? theme.MprcReactPlayer.fullscreenOutTitle : theme.MprcReactPlayer.fullscreenInTitle}
                        >
                            <svg style={styles.icon} viewBox="0 0 24 24">
                                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                            </svg>
                        </button>
                    }
                </div>
            </div>
        </div>
    );
});

export const MprcReactPlayer = MprcReactPlayerComponent;